import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import { updateUser } from "../api/workOrder";
import { COUNTRY_CODES } from "../constants/CountryCode";

const UpdateProfileModal = ({ isOpen, onClose, onProfileUpdated, user }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    countryCode: "+1",
    phoneNumber: "",
  });
  const [initialFormData, setInitialFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form when user data changes
  useEffect(() => {
    if (user && isOpen) {
      let code = "+1";
      let number = user.phone || "";

      if (number.startsWith("+")) {
        const found = COUNTRY_CODES.find((c) => number.startsWith(c.code));
        if (found) {
          code = found.code;
          number = number.slice(found.code.length).replace(/\D/g, "");
        }
      } else {
        number = number.replace(/\D/g, "");
      }

      const initialData = {
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        countryCode: code,
        phoneNumber: number.slice(0, 15), // Limit to 15 digits
      };

      setFormData(initialData);
      setInitialFormData(initialData);
      setErrors({});
      setTouched({});
      setHasChanges(false);
    }
  }, [user, isOpen]);

  // Detect form changes
  useEffect(() => {
    const changed =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setHasChanges(changed);
  }, [formData, initialFormData]);

  // Validation functions
  const validateFirstName = (value) =>
    value.trim() ? "" : "First Name is required";

  const validateLastName = (value) =>
    value.trim() ? "" : "Last Name is required";

  const validatePhoneNumber = (value, countryCode) => {
    const digits = value.replace(/\D/g, "");
    const minDigits = countryCode === "+1" ? 10 : 5;
    const maxDigits = 15;

    if (!digits) return "Phone number is required";
    if (digits.length < minDigits)
      return `At least ${minDigits} digits required`;
    if (digits.length > maxDigits) return `Max ${maxDigits} digits allowed`;
    return "";
  };

  const validateForm = () => {
    const newErrors = {
      firstName: validateFirstName(formData.firstName),
      lastName: validateLastName(formData.lastName),
      phoneNumber: validatePhoneNumber(
        formData.phoneNumber,
        formData.countryCode
      ),
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((err) => err === "");
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let val = value;

    if (name === "phoneNumber") {
      val = value.replace(/\D/g, "").slice(0, 15);
    }

    setFormData((prev) => ({ ...prev, [name]: val }));

    // Clear error if field is touched
    if (touched[name]) {
      let error = "";
      if (name === "firstName") error = validateFirstName(val);
      if (name === "lastName") error = validateLastName(val);
      if (name === "phoneNumber")
        error = validatePhoneNumber(val, formData.countryCode);

      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Handle blur (show error only after leaving field)
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    let error = "";
    if (name === "firstName") error = validateFirstName(formData.firstName);
    if (name === "lastName") error = validateLastName(formData.lastName);
    if (name === "phoneNumber")
      error = validatePhoneNumber(formData.phoneNumber, formData.countryCode);

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all as touched
    setTouched({
      firstName: true,
      lastName: true,
      phoneNumber: true,
    });

    if (!validateForm()) {
      toast.error("Please fix the errors below.", {
        duration: 4000,
        position: "top-right",
      });
      return;
    }

    if (!hasChanges) {
      toast.info("No changes to save.");
      return;
    }

    setIsUpdating(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: `${formData.countryCode}${formData.phoneNumber}`,
      };

      const response = await updateUser(user.id, payload);

      if (!response?.message) {
        throw new Error("No success message from server");
      }

      toast.success("Profile updated successfully!", {
        duration: 4000,
        position: "top-right",
      });

      onProfileUpdated();
      onClose();
    } catch (error) {
      console.error("Update Profile Error:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update profile";
      toast.error(msg, { duration: 5000, position: "top-right" });
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 transition-opacity duration-300"
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-profile-title"
    >
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-md">
        <h4 id="update-profile-title" className="text-2xl font-bold mb-6">
          Update Profile
        </h4>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          {/* First Name */}
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="John"
              aria-required="true"
              aria-invalid={!!errors.firstName && touched.firstName}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm ${
                errors.firstName && touched.firstName
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.firstName && touched.firstName && (
              <p className="text-red-500 text-xs mt-1 flex items-center">
                {errors.firstName}
              </p>
            )}
          </div>

          {/* Last Name */}
          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              value={formData.lastName}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Doe"
              aria-required="true"
              aria-invalid={!!errors.lastName && touched.lastName}
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm ${
                errors.lastName && touched.lastName
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
            {errors.lastName && touched.lastName && (
              <p className="text-red-500 text-xs mt-1"> {errors.lastName}</p>
            )}
          </div>

          {/* Phone Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="w-28 p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Country code"
              >
                {COUNTRY_CODES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </select>

              <input
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="1234567890"
                maxLength={15}
                aria-label="Phone number"
                aria-required="true"
                aria-invalid={!!errors.phoneNumber && touched.phoneNumber}
                className={`flex-1 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors text-sm ${
                  errors.phoneNumber && touched.phoneNumber
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />
            </div>
            {errors.phoneNumber && touched.phoneNumber && (
              <p className="text-red-500 text-xs mt-1 flex items-center">
                {errors.phoneNumber}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
              aria-label="Cancel"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isUpdating || !hasChanges}
              className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors flex items-center gap-2 ${
                isUpdating || !hasChanges
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-black hover:bg-gray-900"
              }`}
              aria-label={isUpdating ? "Updating..." : "Update Profile"}
            >
              {isUpdating ? (
                <>
                  <svg
                    className="animate-spin h-4 w-4"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Profile"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateProfileModal;
